import PropTypes from 'prop-types';
import React, {useEffect, useRef} from 'react';
import {FormattedMessage} from 'react-intl';
import VM from 'scratch-vm';

import Box from '../box/box.jsx';
import Modal from '../../containers/modal.jsx';
import Waveform from '../waveform/waveform.jsx';
import imageIcon from '../action-menu/icon--backdrop.svg';
import audioIcon from './icon--sounds-orange.svg';
import audioWhiteIcon from './icon--sounds-white.svg';

import styles from './vision-trainer-modal.css';

const VisionTrainerModal = props => {
    const labelGridRef = useRef(null);
    const previousFirstLabelRef = useRef(props.labels[0] || '');

    useEffect(() => {
        const nextFirstLabel = props.labels[0] || '';
        if (labelGridRef.current && nextFirstLabel && nextFirstLabel !== previousFirstLabelRef.current) {
            labelGridRef.current.scrollTo({
                left: 0,
                behavior: 'smooth'
            });
        }
        previousFirstLabelRef.current = nextFirstLabel;
    }, [props.labels]);

    return (
        <Modal
            className={styles.modalContent}
            contentLabel={
                <FormattedMessage
                    defaultMessage="Datatræner"
                    description="Titel paa datatraener-modalen"
                    id="gui.visionTrainer.title"
                />
            }
            headerClassName={styles.header}
            id="visionTrainer"
            onRequestClose={props.onRequestClose}
        >
            <Box className={styles.body}>
                <div className={styles.cameraPanel}>
                    <video
                        ref={props.videoRef}
                        autoPlay
                        className={styles.videoFrame}
                        muted
                        playsInline
                    />
                    <div className={styles.statusCard}>
                        <div className={styles.statusLine}>{props.status}</div>
                        {props.error ? <div className={styles.error}>{props.error}</div> : null}
                    </div>
                    <div className={styles.predictionModules}>
                        <div className={styles.predictionCard}>
                            <div className={styles.predictionModuleTitle}>
                                <img
                                    alt=""
                                    className={`${styles.predictionModuleIcon} ${styles.predictionImageIcon}`}
                                    src={imageIcon}
                                />
                                <span>Billede</span>
                            </div>
                            <div className={styles.predictionRow}>
                                <span>
                                    <FormattedMessage
                                        defaultMessage="Aktuel klasse"
                                        description="Etiket for den aktuelle dataforudsigelse"
                                        id="gui.visionTrainer.currentLabel"
                                    />
                                </span>
                                <span className={styles.predictionLabel}>
                                    {props.imagePredictionLabel || 'ingen'}
                                </span>
                            </div>
                            <div className={styles.predictionConfidence}>
                                <FormattedMessage
                                    defaultMessage="Sikkerhed"
                                    description="Etiket for sikkerheden paa den aktuelle dataforudsigelse"
                                    id="gui.visionTrainer.confidence"
                                />: {Math.round((props.imagePredictionConfidence || 0) * 100)}%
                            </div>
                        </div>
                        <div className={styles.predictionCard}>
                            <div className={styles.predictionModuleTitle}>
                                <img
                                    alt=""
                                    className={styles.predictionModuleIcon}
                                    src={audioIcon}
                                />
                                <span>Lyd</span>
                            </div>
                            <div className={styles.predictionRow}>
                                <span>
                                    <FormattedMessage
                                        defaultMessage="Aktuel klasse"
                                        description="Etiket for den aktuelle dataforudsigelse"
                                        id="gui.visionTrainer.currentLabel"
                                    />
                                </span>
                                <span className={styles.predictionLabel}>
                                    {props.audioPredictionLabel || 'ingen'}
                                </span>
                            </div>
                            <div className={styles.predictionConfidence}>
                                <FormattedMessage
                                    defaultMessage="Sikkerhed"
                                    description="Etiket for sikkerheden paa den aktuelle dataforudsigelse"
                                    id="gui.visionTrainer.confidence"
                                />: {Math.round((props.audioPredictionConfidence || 0) * 100)}%
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.controlPanel}>
                    <div className={styles.classCreator}>
                        <div className={styles.classCreatorRow}>
                            <input
                                className={styles.classInput}
                                onChange={props.onDraftLabelChange}
                                onKeyDown={props.onDraftLabelKeyDown}
                                placeholder="nyt klassenavn"
                                type="text"
                                value={props.draftLabel}
                            />
                            <button
                                className={styles.button}
                                onClick={props.onAddLabel}
                            >
                                <span className={styles.trainClassLabel}>Træn klasse</span>
                                <span className={styles.hiddenText}>
                                <FormattedMessage
                                    defaultMessage="Tilføj klasse"
                                    description="Knaptekst for at oprette en ny traeningsklasse"
                                    id="gui.visionTrainer.trainClass"
                                />
                                </span>
                            </button>
                        </div>
                        {props.classTypeChoiceVisible ? (
                            <div className={styles.typeChoicePopup}>
                                <button
                                    className={styles.typeChoiceCloseButton}
                                    onClick={props.onCancelClassTypeChoice}
                                    title="Luk"
                                    type="button"
                                >
                                    x
                                </button>
                                <div className={styles.typeChoiceTitle}>
                                    Vælg hvad klassen skal trænes på
                                </div>
                                <div className={styles.typeChoiceActions}>
                                    <button
                                        className={styles.button}
                                        onClick={props.onCreateImageLabel}
                                        type="button"
                                    >
                                        <img
                                            alt=""
                                            className={`${styles.typeChoiceIcon} ${styles.typeChoiceImageIcon}`}
                                            src={imageIcon}
                                        />
                                        Billede
                                    </button>
                                    <button
                                        className={styles.button}
                                        onClick={props.onCreateAudioLabel}
                                        type="button"
                                    >
                                        <img
                                            alt=""
                                            className={styles.typeChoiceIcon}
                                            src={audioWhiteIcon}
                                        />
                                        Lyd
                                    </button>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div
                        className={styles.labelGrid}
                        ref={labelGridRef}
                    >
                        {props.labels.map(label => (
                            <div
                                key={label}
                                className={styles.labelCard}
                            >
                                {(() => {
                                    const isAudioLabel = props.labelTypes[label] === 'audio';
                                    return (
                                        <React.Fragment>
                                <div className={styles.labelHeader}>
                                    <span className={styles.labelTitle}>
                                        <img
                                            alt=""
                                            className={`${styles.labelTypeIcon} ${
                                                isAudioLabel ? '' : styles.labelImageIcon
                                            }`}
                                            src={isAudioLabel ? audioIcon : imageIcon}
                                        />
                                        <span className={styles.labelName}>{label}</span>
                                    </span>
                                    <button
                                        className={styles.removeLabelButton}
                                        onClick={() => props.onRemoveLabel(label)}
                                        title="Fjern klasse"
                                        type="button"
                                    >
                                        ×
                                    </button>
                                </div>
                                <span className={styles.labelCount}>
                                    {props.trainingCounts[label] || 0} eksempler · {isAudioLabel ? 'lyd' : 'billede'}
                                </span>
                                <div className={styles.labelActions}>
                                    <button
                                        className={styles.button}
                                        disabled={!isAudioLabel && (!props.modelReady || props.isInitializing)}
                                        onClick={isAudioLabel ? () => props.onStartAddingExamples(label) : null}
                                        onMouseDown={isAudioLabel ? null : () => props.onStartAddingExamples(label)}
                                        onMouseLeave={isAudioLabel ? null : props.onStopAddingExamples}
                                        onMouseUp={isAudioLabel ? null : props.onStopAddingExamples}
                                        onTouchEnd={isAudioLabel ? null : props.onStopAddingExamples}
                                        onTouchStart={isAudioLabel ? null : () => props.onStartAddingExamples(label)}
                                        type="button"
                                    >
                                        {isAudioLabel ? 'Optag lyd' : (
                                            <FormattedMessage
                                                defaultMessage="Tag et billede"
                                                description="Knaptekst for at tage et traeningsbillede med kameraet"
                                                id="gui.visionTrainer.addExample"
                                            />
                                        )}
                                    </button>
                                    <label
                                        className={`${styles.secondaryButton} ${styles.uploadButton}`}
                                        htmlFor={`visionTrainerUpload-${encodeURIComponent(label)}`}
                                    >
                                        {isAudioLabel ? 'Upload lyd' : (
                                            <FormattedMessage
                                                defaultMessage="Upload billede"
                                                description="Knaptekst for at uploade traeningsbilleder fra computeren"
                                                id="gui.visionTrainer.uploadExample"
                                            />
                                        )}
                                        <input
                                            accept={isAudioLabel ? 'audio/*' : 'image/*'}
                                            className={styles.uploadInput}
                                            disabled={props.isInitializing}
                                            id={`visionTrainerUpload-${encodeURIComponent(label)}`}
                                            multiple
                                            onChange={event => props.onUploadExamples(label, event)}
                                            type="file"
                                        />
                                    </label>
                                </div>
                                {props.examplesByLabel[label] && props.examplesByLabel[label].length ? (
                                    <div className={styles.exampleGallery}>
                                        {props.examplesByLabel[label].map(example => (
                                            <div
                                                key={example.id}
                                                className={styles.exampleCard}
                                            >
                                                {isAudioLabel ? (
                                                    <div className={styles.audioExampleThumb}>
                                                        {example.waveform && example.waveform.length ? (
                                                            <Waveform
                                                                data={example.waveform}
                                                                fill="#ffd08a"
                                                                height={64}
                                                                level={0}
                                                                stroke="#ff8c1a"
                                                                width={96}
                                                            />
                                                        ) : (
                                                            <span>lyd</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <img
                                                        alt={label}
                                                        className={styles.exampleThumb}
                                                        src={example.previewUrl}
                                                    />
                                                )}
                                                <button
                                                    className={styles.exampleDelete}
                                                    onClick={() => props.onRemoveExample(label, example.id)}
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Slet"
                                                        description="Knaptekst for at slette eet traeningsbillede"
                                                        id="gui.visionTrainer.deleteExample"
                                                    />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                                        </React.Fragment>
                                    );
                                })()}
                            </div>
                        ))}
                    </div>

                    <div className={styles.buttonRow}>
                        <button
                            className={styles.button}
                            disabled={
                                (!props.hasLabels || !props.hasExamples || props.isInitializing ||
                                props.trainingProgressVisible) &&
                                !props.isPredicting
                            }
                            onClick={props.onTogglePrediction}
                        >
                            {props.isPredicting ? (
                                <FormattedMessage
                                    defaultMessage="Stop forudsigelse"
                                    description="Knaptekst for at stoppe live-forudsigelse"
                                    id="gui.visionTrainer.stopPrediction"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Start træning"
                                    description="Knaptekst for at starte datatraening foer live-forudsigelse"
                                    id="gui.visionTrainer.startPrediction"
                                />
                            )}
                        </button>
                        <button
                            className={styles.secondaryButton}
                            disabled={!props.hasExamples && !props.currentLabel}
                            onClick={props.onClearTraining}
                        >
                            <FormattedMessage
                                defaultMessage="Ryd træning"
                                description="Knaptekst for at rydde datatraeningen"
                                id="gui.visionTrainer.clearTraining"
                            />
                        </button>
                        {props.trainingProgressVisible ? (
                            <div className={styles.trainingProgress}>
                                <div className={styles.trainingProgressMessage}>
                                    {props.trainingProgressMessage}
                                </div>
                                <div
                                    aria-hidden
                                    className={styles.trainingProgressTrack}
                                >
                                    <div
                                        className={styles.trainingProgressFill}
                                        style={{width: `${props.trainingProgress}%`}}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.startCodingRow}>
                        <button
                            className={styles.startCodingButton}
                            onClick={props.onRequestClose}
                        >
                            <FormattedMessage
                                defaultMessage="Begynd at kode"
                                description="Knaptekst for at lukke datatraeneren og begynde at kode"
                                id="gui.visionTrainer.startCoding"
                            />
                        </button>
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

VisionTrainerModal.propTypes = {
    activeInputType: PropTypes.string,
    audioPredictionConfidence: PropTypes.number,
    audioPredictionLabel: PropTypes.string,
    classTypeChoiceVisible: PropTypes.bool,
    currentConfidence: PropTypes.number,
    currentLabel: PropTypes.string,
    draftLabel: PropTypes.string,
    error: PropTypes.string,
    examplesByLabel: PropTypes.object,
    hasExamples: PropTypes.bool,
    hasLabels: PropTypes.bool,
    imagePredictionConfidence: PropTypes.number,
    imagePredictionLabel: PropTypes.string,
    isInitializing: PropTypes.bool,
    isPredicting: PropTypes.bool,
    labelTypes: PropTypes.object,
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    modelReady: PropTypes.bool,
    onActiveInputTypeChange: PropTypes.func.isRequired,
    onAddLabel: PropTypes.func.isRequired,
    onCancelClassTypeChoice: PropTypes.func.isRequired,
    onClearTraining: PropTypes.func.isRequired,
    onCreateAudioLabel: PropTypes.func.isRequired,
    onCreateImageLabel: PropTypes.func.isRequired,
    onDraftLabelChange: PropTypes.func.isRequired,
    onDraftLabelKeyDown: PropTypes.func.isRequired,
    onRemoveExample: PropTypes.func.isRequired,
    onRemoveLabel: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onStartAddingExamples: PropTypes.func.isRequired,
    onStopAddingExamples: PropTypes.func.isRequired,
    onTogglePrediction: PropTypes.func.isRequired,
    onUploadExamples: PropTypes.func.isRequired,
    status: PropTypes.string,
    trainingCounts: PropTypes.object,
    trainingProgress: PropTypes.number,
    trainingProgressMessage: PropTypes.string,
    trainingProgressVisible: PropTypes.bool,
    videoRef: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM)
};

export default VisionTrainerModal;
